import { useState, useEffect, useRef } from "react";
import { UserMenu } from "@/frontend/components/UserMenu";
import { ChatList } from "@/frontend/components/ChatList";
import { SaleIncidentList } from "@/frontend/components/SaleIncidentList";
import { Chat, type ChatRef } from "@/frontend/components/Chat";
import { SaleIncident } from "@/frontend/components/SaleIncident";

interface SalesPageProps {
  onNavigate: (path: string) => void;
}

type ViewMode = "chats" | "incidents";

const STORAGE_KEY_VIEW_MODE = "salesPage.viewMode";

function loadViewMode(): ViewMode {
  try {
    const saved = localStorage.getItem(STORAGE_KEY_VIEW_MODE);
    return saved === "incidents" ? "incidents" : "chats";
  } catch {
    return "chats";
  }
}

function saveViewMode(mode: ViewMode): void {
  try {
    localStorage.setItem(STORAGE_KEY_VIEW_MODE, mode);
  } catch {
    // Silent fail
  }
}

export function SalesPage({ onNavigate }: SalesPageProps) {
  const [viewMode, setViewMode] = useState<ViewMode>(loadViewMode);
  const [selectedChatId, setSelectedChatId] = useState<string | undefined>(
    undefined
  );
  const [selectedIncidentId, setSelectedIncidentId] = useState<
    number | undefined
  >(undefined);
  const [initialChatId, setInitialChatId] = useState<string | undefined>(
    undefined
  );
  const [initialIncidentId, setInitialIncidentId] = useState<
    number | undefined
  >(undefined);
  const chatRef = useRef<ChatRef>(null);

  useEffect(() => {
    document.title = "Sales";

    const params = new URLSearchParams(window.location.search);
    const chatId = params.get("chat");
    const incidentId = params.get("incident");

    if (chatId) {
      setSelectedChatId(chatId);
      setInitialChatId(chatId);
    }

    if (incidentId) {
      const parsedIncidentId = parseInt(incidentId);
      if (!isNaN(parsedIncidentId)) {
        setSelectedIncidentId(parsedIncidentId);
        setInitialIncidentId(parsedIncidentId);
        setViewMode("incidents");
      }
    }
  }, []);

  useEffect(() => {
    saveViewMode(viewMode);
  }, [viewMode]);

  const handleChatSelect = (chatId: string) => {
    setSelectedChatId(chatId);

    const url = new URL(window.location.href);
    url.searchParams.set("chat", chatId);
    url.searchParams.delete("incident");
    window.history.pushState({}, "", url.toString());

    setSelectedIncidentId(undefined);
  };

  const handleIncidentSelect = (
    incidentId: number,
    chatId: string,
    incidentDate: string
  ) => {
    setSelectedChatId(chatId);
    setSelectedIncidentId(incidentId);

    const url = new URL(window.location.href);
    url.searchParams.set("chat", chatId);
    url.searchParams.set("incident", incidentId.toString());
    window.history.pushState({}, "", url.toString());

    setTimeout(() => {
      chatRef.current?.scrollToDate(incidentDate);
    }, 300);
  };

  const handleScrollToDate = (date: string) => {
    chatRef.current?.scrollToDate(date);
  };

  return (
    <>
      <UserMenu />
      <div className="h-screen p-8 overflow-hidden">
        <div className="grid grid-cols-[2.5fr_4.5fr_3fr] gap-4 h-full">
          {viewMode === "chats" ? (
            <ChatList
              onChatSelect={handleChatSelect}
              selectedChatId={selectedChatId}
              initialScrollToChatId={initialChatId}
              accountFilter={["sales-2", "sales-3", "dev"]}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
            />
          ) : (
            <SaleIncidentList
              onIncidentSelect={handleIncidentSelect}
              selectedIncidentId={selectedIncidentId}
              initialScrollToIncidentId={initialIncidentId}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
            />
          )}
          <Chat ref={chatRef} selectedChatId={selectedChatId} />
          <SaleIncident
            selectedChatId={selectedChatId}
            selectedIncidentId={selectedIncidentId}
            onScrollToDate={handleScrollToDate}
          />
        </div>
      </div>
    </>
  );
}
